import React from 'react';
import Link from 'next/link';
import { Heading3, Link as BlueprintLink, Text } from '@blueprint/ui';
import { VisuallyHidden } from 'shared/components';
import HistoryTable from 'listing-details/components/shared/HistoryTable/HistoryTable';
import RecentSalesLink from 'listing-details/components/shared/RecentSalesLink/RecentSalesLink';
import { trackRecentSaleLink } from './MarketStats.tracking';
import { MarketStatsProps, MarketStatsFragment } from './MarketStats.types';
import {
    getArea,
    getEstimatedValue,
    getPropertyType,
    getRecentSales,
    getRentalEstimate,
    getSalesSummary,
    formatPrice
} from './MarketStats.helpers';
import {
    StyledHeading2,
    StatsSection,
    RentalOpportunitiesSection,
    RecentSalesSection,
    StatsList,
    StatName,
    StatValues,
    IconIncrease,
    IconDecrease
} from './MarketStats.styles';

const MarketStats = ({ listingSection, data }: MarketStatsProps) => {
    const isSale = ['for-sale', 'new-homes'].includes(listingSection);
    const isRental = ['to-rent'].includes(listingSection);

    return (
        <article>
            <StyledHeading2 size="6">Market stats</StyledHeading2>
            {isSale && <MarketStatsSale data={data} />}
            {isRental && <MarketStatsRental data={data} />}
        </article>
    );
};

const MarketStatsSale = ({ data }: { data: MarketStatsFragment }) => {
    const area = getArea(data);
    const propertyType = getPropertyType(data);
    const hasMarketStats = Boolean(data.marketStats);
    const hasRecentSales = Boolean(data.marketStats?.recentSales.length);

    return (
        <>
            <StatsSection data-testid="marketstats-historical-estimate">
                <Heading3 size="7">Over last 12 months</Heading3>
                {hasMarketStats ? (
                    <Stats data={data} />
                ) : (
                    <Text>Not known</Text>
                )}
            </StatsSection>

            <RentalOpportunitiesSection data-testid="marketstats-rental-opportunities">
                <Heading3 size="7">Rental opportunities</Heading3>
                {hasMarketStats ? (
                    <RentalOpportunities
                        data={data}
                        copy={`Looking for an investment? The average rent for a ${propertyType.full} in ${area.name} is currently:`}
                    />
                ) : (
                    <Text>Not known</Text>
                )}
            </RentalOpportunitiesSection>

            <div data-testid="marketstats-recent-sales">
                <RecentSalesSection>
                    <Heading3 size="7">Recent sales nearby</Heading3>
                    {hasRecentSales ? (
                        <RecentSales data={data} />
                    ) : (
                        <Text>Not known</Text>
                    )}
                </RecentSalesSection>

                {hasMarketStats && hasRecentSales && (
                    <RecentSalesLink
                        areaName={area.name || ''}
                        areaUri={area.uri || ''}
                        trackingLabel="house prices link"
                    />
                )}
            </div>
        </>
    );
};

const MarketStatsRental = ({ data }: { data: MarketStatsFragment }) => {
    const area = getArea(data);
    const propertyType = getPropertyType(data);
    const hasMarketStats = Boolean(data.marketStats);

    return (
        <>
            <RentalOpportunitiesSection data-testid="marketstats-rental-activity">
                <Heading3 size="7">Rental activity</Heading3>
                {hasMarketStats ? (
                    <RentalOpportunities
                        data={data}
                        copy={`The average rent for a ${propertyType.full} in ${area.name} is currently:`}
                    />
                ) : (
                    <Text>Not known</Text>
                )}
            </RentalOpportunitiesSection>
        </>
    );
};

const Stats = ({ data }: { data: MarketStatsFragment }) => {
    const area = getArea(data);
    const propertyType = getPropertyType(data);
    const estimatedValue = getEstimatedValue(data);
    const salesSummary = getSalesSummary(data);

    const hasPriceIncreased = (estimatedValue.changeValue || 0) >= 0;
    const PriceChangeIcon = hasPriceIncreased ? IconIncrease : IconDecrease;
    const priceChangeSign = hasPriceIncreased ? '+' : '-';

    return (
        <StatsList>
            <li>
                <StatName>
                    {`Average estimated value for a ${propertyType.short} in ${area.name}`}
                </StatName>
                <StatValues>
                    <Text as="span" size="7" isBold={true} color="grey-slate">
                        {estimatedValue.value
                            ? formatPrice(estimatedValue.value)
                            : 'Not known'}
                    </Text>{' '}
                    {estimatedValue.changeValue !== null && (
                        <>
                            <VisuallyHidden>
                                which is a change of
                            </VisuallyHidden>{' '}
                            <PriceChangeIcon aria-hidden="true" />{' '}
                            <Text as="span">
                                {`
                                        ${priceChangeSign}${formatPrice(
                                    Math.abs(estimatedValue.changeValue)
                                )} (${priceChangeSign}${Math.abs(
                                    estimatedValue.changePercentage || 0
                                )}%)
                                    `}
                            </Text>
                        </>
                    )}
                </StatValues>
            </li>
            <li>
                <StatName>Average sale price</StatName>
                <StatValues>
                    <Text as="span" size="7" isBold={true} color="grey-slate">
                        {salesSummary.averageSalesPrice
                            ? formatPrice(salesSummary.averageSalesPrice)
                            : 'Not known'}
                    </Text>
                </StatValues>
            </li>
            <li>
                <StatName>Properties sold</StatName>
                <StatValues>
                    <Text as="span" size="7" isBold={true} color="grey-slate">
                        {salesSummary.propertiesSold
                            ? salesSummary.propertiesSold
                            : 'Not known'}
                    </Text>
                </StatValues>
            </li>
        </StatsList>
    );
};

const RentalOpportunities = ({
    data,
    copy
}: {
    data: MarketStatsFragment;
    copy: string;
}) => {
    const rentalEstimate = getRentalEstimate(data);

    return (
        <p>
            <Text as="span">{copy}</Text>
            <Text as="span" color="text-title" size="8" isBold={true}>
                {rentalEstimate
                    ? `${formatPrice(rentalEstimate)} pcm`
                    : 'Not known'}
            </Text>
        </p>
    );
};

const RecentSales = ({ data }: { data: MarketStatsFragment }) => {
    const recentSales = getRecentSales(data, 3);

    return (
        <HistoryTable
            headings={['Date', 'Address', 'Sale']}
            data={recentSales.map((sale) => [
                sale.date,
                <Link href={sale.uri} key={sale.uri}>
                    <BlueprintLink
                        onClick={trackRecentSaleLink}
                        href={sale.uri}
                    >
                        {sale.address}
                    </BlueprintLink>
                </Link>,
                formatPrice(sale.price)
            ])}
        />
    );
};
export default MarketStats;
